import {
	ReactElement,
	useMemo,
	useState
} from "react";
import {
	AlertBar,
	Button,
	Icon,
	SearchInput,
	SearchPlaceholder,
	TableView,
	TableViewNoResultConfig
} from "client/Components";
import {
	createLocalDataSource,
	useSelection,
	useSortHandler
} from "client/Hooks";
import { contains } from "client/Data/searching";
import {
	DataSourceSortableState,
	DataSourceSortDirection
} from "client/Data/sorting";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { fromArray } from "client_legacy/ndaw/mapHelpers";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	IntegrationFlowSettingSaveModel,
	IntegrationSettingScopeKind
} from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import { IntegrationSharedSettingOptionModel } from "client/Api/IntegrationBuilder/integrationBuilderApps";
import { isNil, resolveFormattedTextString } from "client/Misc/utils";
import styles from "./SettingsTab.module.less";
import { InputValidationResultKind } from "client/Validation";
import { MAX_SETTINGS_COUNT } from "./EditIntegrationFlowPage";

export type SettingsTabProps = {
	attachable: IValueInputAttachable<readonly IntegrationFlowSettingSaveModel[]>;
	sharedSettings: readonly IntegrationSharedSettingOptionModel[];
	indicesWithServerErrors: ReadonlySet<number>;
	selectedItems: readonly SettingItem[];
	onSelectedItemsChange: (selectedItems: readonly SettingItem[]) => void;
	onOpenSettingsDialog: (item?: IntegrationFlowSettingSaveModel) => void;
	isReadOnly: boolean;
};

export const SettingsTab = (props: SettingsTabProps): ReactElement => {
	const {
		attachable,
		sharedSettings,
		indicesWithServerErrors,
		selectedItems,
		onSelectedItemsChange,
		onOpenSettingsDialog,
		isReadOnly
	} = props;

	const [searchTerm, setSearchTerm] = useState<string>("");
	const [isInfoAlertDismissed, setIsInfoAlertDismissed] = useState(false);
	const [sortingState, setSortingState] = useState<DataSourceSortableState<SettingItem>>({
		propertyName: "key",
		direction: DataSourceSortDirection.Ascending
	});

	const { sortingFor } = useSortHandler<SettingItem>(sortingState, setSortingState);

	const settings: readonly IntegrationFlowSettingSaveModel[] = attachable.value;

	const sharedSettingsById: ReadonlyMap<number, IntegrationSharedSettingOptionModel> = useMemo(
		() => fromArray(sharedSettings, x => x.id),
		[sharedSettings]
	);

	const items = useMemo(
		(): readonly SettingItem[] => settings.map(
			(setting): SettingItem => toSettingItem(setting, sharedSettingsById)
		),
		[settings, sharedSettingsById]
	);

	const {
		currentItems
	} = useSettingsDataSource(
		items,
		{
			searchTerm,
			sorting: sortingState
		}
	);

	const {
		multiSelection,
		selectionFor
	} = useSelection(
		currentItems,
		"key",
		{
			isMultiSelect: true,
			selectedItems,
			setSelectedItems: onSelectedItemsChange
		}
	);

	const noResultConfig = useMemo(
		(): TableViewNoResultConfig => {
			if (searchTerm !== "") {
				return {
					icon: "magnifying-glass",
					title: localize("_NoSearchResults"),
					message: localize("_PleaseTrySearchingWithAnotherTerm")
				};
			}

			return {
				icon: "gear",
				title: localize("_NoSettingsYet"),
				message: localize("_ThereAreNoSettingsYet")
			};
		},
		[searchTerm]
	);

	return (
		<div className={styles.root}>
			{!isInfoAlertDismissed && (
				<AlertBar
					isInline
					kind="info"
					isDismissable
					onDismiss={() => setIsInfoAlertDismissed(true)}
					trackingId="integration-settings-info"
				>
					{localize("_IntegrationSettingsInfo")}
				</AlertBar>
			)}
			{!isReadOnly && (
				<div>
					<Button
						kind="secondary"
						text={localize("_NewSetting")}
						icon="plus"
						isDisabled={settings.length >= MAX_SETTINGS_COUNT}
						tooltip={settings.length >= MAX_SETTINGS_COUNT
							? resolveFormattedTextString({
								template: localize("_IntegrationFlowSettingsMaxCountErrorMessage"),
								replacements: [
									{
										what: "{{MaxCount}}",
										filler: MAX_SETTINGS_COUNT.toString()
									}
								]
							})
							: undefined
						}
						onClick={() => onOpenSettingsDialog()}
						trackingId="new-setting"
					/>
				</div>
			)}
			<SearchInput
				onChange={setSearchTerm}
				trackingId="integration-settings-search"
				icon="filter"
				height="large"
				placeholder={(
					<SearchPlaceholder
						kind="filter"
						properties={[
							localize("_Key"),
							localize("_Value"),
							localize("_DisplayName")
						]}
					/>
				)}
			/>

			<div className={styles.table}>
				<TableView
					multiSelectable={!isReadOnly && multiSelection}
					items={currentItems}
					itemKey="key"
					height="fill"
					trackingId="integration-settings-table"
					noResultConfig={noResultConfig}
					columns={[
						{
							title: localize("_Key"),
							trackingId: "key",
							width: "fill",
							sortable: sortingFor("key")
						},
						{
							title: localize("_Scope"),
							trackingId: "scope",
							width: "fill",
							sortable: sortingFor("scope")
						},
						{
							title: localize("_Value"),
							trackingId: "value",
							width: "fill",
							sortable: sortingFor("value")
						},
						{
							title: localize("_DisplayName"),
							trackingId: "display-name",
							width: "fill",
							sortable: sortingFor("displayName")
						},
						{
							title: localize("_Description"),
							trackingId: "description",
							width: "fill",
							sortable: sortingFor("description")
						}
					]}
				>
					{(item): ReactElement => {
						const index: number = settings.findIndex(x => x.key === item.key);
						const hasValidationError: boolean = index !== -1
							&& (
								attachable.forIndex(index)
									.validation
									.getValidationState()
									.kind === InputValidationResultKind.Invalid
								|| indicesWithServerErrors.has(index)
							);

						return (
							<TableView.Row
								selectable={!isReadOnly && selectionFor(item)}
								trackingId={item.key}
								onExecute={() => onOpenSettingsDialog(item.setting)}
							>
								<TableView.Cell>
									{item.key}
									{hasValidationError && (
										<Icon value={{ kind: "font", name: "triangle-exclamation", color: "danger" }} />
									)}
								</TableView.Cell>
								<TableView.Cell>
									{item.scope}
								</TableView.Cell>
								<TableView.Cell>
									{item.value}
								</TableView.Cell>
								<TableView.Cell>
									{item.displayName}
								</TableView.Cell>
								<TableView.Cell>
									{item.description}
								</TableView.Cell>
							</TableView.Row>
						);
					}}
				</TableView>
			</div>
		</div>
	);
};
SettingsTab.displayName = "SettingsTab";

export type SettingItem = {
	key: string;
	scope: string;
	value: string;
	displayName: string;
	description: string;
	setting: IntegrationFlowSettingSaveModel;
};

function toSettingItem(
	setting: IntegrationFlowSettingSaveModel,
	sharedSettingsById: ReadonlyMap<number, IntegrationSharedSettingOptionModel>
): SettingItem {
	switch (setting.scopeKind) {
		case IntegrationSettingScopeKind.ThisFlow:
			return {
				key: setting.key,
				scope: localize("_ThisFlow"),
				value: setting.value,
				displayName: setting.displayName,
				description: setting.description,
				setting
			};
		case IntegrationSettingScopeKind.Shared: {
			const sharedSetting: IntegrationSharedSettingOptionModel | undefined = sharedSettingsById
				.get(setting.sharedSettingId);

			if (isNil(sharedSetting)) {
				throw new Error(`No shared setting exists with id '${setting.sharedSettingId}'.`);
			}

			return {
				key: setting.key,
				scope: localize("_SharedSetting"),
				value: sharedSetting.value,
				displayName: sharedSetting.displayName,
				description: sharedSetting.description ?? "",
				setting
			};
		}
		default:
			return never(setting);
	}
}

const useSettingsDataSource = createLocalDataSource<SettingItem>()
	.setConfiguration({
		search: {
			doesMatchItem: searchMatchFunction
		}
	})
	.getHook();

function searchMatchFunction(
	item: SettingItem,
	normalizedNotEmptySearchTerm: Exclude<string, "">
): boolean {
	return contains(item.key, normalizedNotEmptySearchTerm)
		|| contains(item.value, normalizedNotEmptySearchTerm)
		|| contains(item.displayName, normalizedNotEmptySearchTerm);
}
